import CoreFactory, { Glue42Core } from "@glue42/core";
import { ActivityModule } from "./activity/main";
import AppManagerFactory from "./app-manager/main";
import WindowsFactory from "./windows/main";
import LayoutsFactory from "./layouts/main";
import DisplayManager from "./displays/displayManager";
import { factory as channels } from "./channels/main";
import { factory as hotkeys } from "./hotkeys/main";

import { Glue42 } from "../glue";
import { version } from "../package.json";
import prepareConfig from "./config";
import { LibConfigObject } from "./types";
import { ActivityMode } from "./activity/activityConfig";
import Utils from "./utils";
import { Notifications } from "./notifications/main";
import { factory as themes } from "./themes/main";
import { Intents } from "./intents/intents";
import { FactoryCallInfo } from "./factoryCallInfo";
import { Prefs } from "./prefs/index";
import { factory as cookies } from "./cookies/main";
import { EventsDispatcher } from "./eventsDispatcher";

const callInfo: FactoryCallInfo = new FactoryCallInfo();

const factory = async (options: Glue42.Config): Promise<Glue42.Glue> => {
    let firstRun = false;
    if (!callInfo.initialized) {
        firstRun = true;
        callInfo.init(options);
    }

    // if running in GD, do not allow creating multiple instance of this API in the same page
    const glue42gd: Glue42.GDObject = typeof window !== "undefined" && window.glue42gd;
    if (glue42gd) {
        if (!firstRun) {
            callInfo.addCall(options);
            return callInfo.promise;
        }
    }
    const g = await factoryCore(options, glue42gd);
    callInfo.resolve(g);
    return g;
};

const factoryCore = async (options: Glue42.Config, glue42gd: Glue42.GDObject): Promise<Glue42.Glue> => {
    const T42GDExecuteMethod = "T42.GD.Execute";
    const gdMajorVersion = Utils.getGDMajorVersion();
    // get config object
    // basically selecting some properties (and defaulting them)
    // it's used to extract things like layouts, appManager, activities etc things
    // that glueCore itself doesn't know about
    options = options || {};
    const glueConfig = prepareConfig(options);
    options.gateway = options.gateway || {};

    let _appManager: any;
    let _activity: any;
    let _windows: any;
    let _displays: any;
    let _channels: Glue42.Channels;
    const _browserEventsDispatcher = new EventsDispatcher(glueConfig);

    // dependency graph:
    //   windows: agm, appManager (via getter)
    //   activities: contexts, agm, logger, windows, appManager (via getter)
    //   appManager: agm, windows, logger, activities
    //   layouts: agm, appManager, logger
    function createWindows(core: Glue42Core.GlueCore) {
        if (glueConfig.windows) {

            const windowsLogger = getLibLogger("windows", core.logger, glueConfig.windows);

            _windows = WindowsFactory(
                core.agm,
                windowsLogger,
                () => {
                    return _appManager;
                },
                () => {
                    return _displays;
                },
                () => {
                    return _channels;
                },
                gdMajorVersion);
            debugLog(_windows);
            return _windows;
        }
    }

    function createActivities(core: Glue42Core.GlueCore) {
        if (glueConfig.activities) {
            if (ActivityModule.checkIsUsingGW3Implementation && ActivityModule.checkIsUsingGW3Implementation(core.connection)) {

                const activityLogger = getLibLogger("activity", core.logger, glueConfig.activities);

                _activity = new ActivityModule({
                    connection: core.connection,
                    contexts: core.contexts,
                    agm: core.agm,
                    logger: activityLogger,
                    logLevel: "info",
                    disableAutoAnnounce: false,
                    disposeRequestHandling: "exit",
                    announcementInfo: null,
                    windows: _windows,
                    appManagerGetter: () => {
                        return _appManager;
                    },
                    mode: glueConfig.activities.mode as ActivityMode,
                    typesToTrack: glueConfig.activities.typesToTrack,
                    activityId: glue42gd?.activityInfo?.activityId,
                    gdMajorVersion
                }).api;
                debugLog(_activity);
                return _activity;
            }
        }
    }

    function createAppManager(core: Glue42Core.GlueCore) {
        if (!glueConfig.appManager) {
            return;
        }

        const logger = getLibLogger("appManager", core.logger, glueConfig.appManager);

        // AppManager v3
        _appManager = AppManagerFactory({
            agm: core.agm,
            windows: _windows,
            logger,
            activities: _activity,
            mode: glueConfig.appManager.mode,
            gdMajorVersion
        });
        debugLog(_appManager);
        return _appManager;
    }

    function createLayouts(core: Glue42Core.GlueCore) {
        if (!glueConfig.layouts) {
            return;
        }

        const logger = getLibLogger("layouts", core.logger, glueConfig.layouts);
        const layoutsConfig = glueConfig.layouts;
        const lay = LayoutsFactory({
            agm: core.agm,
            appManager: _appManager,
            activityGetter: () => _activity,
            logger,
            mode: layoutsConfig.mode,
            autoSaveWindowContext: layoutsConfig.autoSaveWindowContext ?? false,
            gdMajorVersion
        });
        debugLog(lay);
        return lay;
    }

    function createChannels(core: Glue42Core.GlueCore) {
        if (!glueConfig.channels) {
            return;
        }

        if (!core.contexts) {
            // TODO log - notify the user that he can not use channels without context
            return;
        }

        // Logger
        const logger = getLibLogger("channels", core.logger, glueConfig.channels);

        _channels = channels(core.contexts, core.agm, () => _windows, logger);

        debugLog(_channels);
        return _channels;
    }

    function createHotkeys(core: Glue42Core.GlueCore) {
        const hotkeysAPI = hotkeys(core.agm);

        debugLog(hotkeysAPI);
        return hotkeysAPI;
    }

    function createIntents(core: Glue42Core.GlueCore) {
        const intentsAPI = new Intents(core.agm, _windows, core.logger.subLogger("intents"), options, _appManager);

        debugLog(intentsAPI);
        return intentsAPI;
    }

    function createNotifications(core: Glue42Core.GlueCore) {
        const notificationsAPI = new Notifications(core.interop, core.logger).toAPI();

        debugLog(notificationsAPI);
        return notificationsAPI;
    }

    function createDisplaysApi(core: Glue42Core.GlueCore) {
        if (glueConfig.displays) {
            const displaysLogger = getLibLogger("displays", core.logger, glueConfig.displays);

            _displays = new DisplayManager(core.agm, displaysLogger);
            debugLog(_displays);
            return _displays;
        }
    }

    function createThemes(core: Glue42Core.GlueCore) {
        if (!core.contexts) {
            // TODO log - notify the user that he can not use themes without context
            return;
        }
        const themesAPI = themes(core);

        debugLog(themesAPI);
        return themesAPI;
    }

    function createPrefs(core: Glue42Core.GlueCore) {
        const appName = options.application ?? glue42gd?.application ?? core.interop.instance.application;
        const prefs = new Prefs(appName, core.interop);

        debugLog(prefs);
        return prefs;
    }

    function createCookies(core: Glue42Core.GlueCore) {
        const api = cookies(core.interop, T42GDExecuteMethod);

        debugLog(api);
        return api;
    }

    function getLibLogger(loggerName: string, logger: Glue42Core.Logger.API, config: LibConfigObject<string | boolean>) {
        const newLogger = logger.subLogger(loggerName);

        if (config && config.logger) {
            const loggerConfig = config.logger;
            if (loggerConfig.console) {
                newLogger.consoleLevel(loggerConfig.console);
            }
            if (loggerConfig.publish) {
                newLogger.publishLevel(loggerConfig.publish);
            }
            // not supported in GD3
            // if (loggerConfig.metrics) {
            //  newLogger.metricsLevel(loggerConfig.metrics);
            // }
        }

        return newLogger;
    }

    const ext: Glue42Core.Extension = {
        // define extra libraries for glue-core to raise
        libs: [
            { name: "windows", create: createWindows },
            { name: "activities", create: createActivities },
            { name: "appManager", create: createAppManager },
            { name: "layouts", create: createLayouts },
            { name: "channels", create: createChannels },
            { name: "hotkeys", create: createHotkeys },
            { name: "displays", create: createDisplaysApi },
            { name: "intents", create: createIntents },
            { name: "notifications", create: createNotifications },
            { name: "themes", create: createThemes },
            { name: "prefs", create: createPrefs },
            { name: "cookies", create: createCookies }
        ],

        version,

        enrichGlue: (glue: Glue42Core.GlueCore) => {
            // put some data to config
            glue.config.activities = glueConfig.activities;
            glue.config.windows = glueConfig.windows;
            glue.config.appManager = glueConfig.appManager;
            glue.config.layouts = glueConfig.layouts;
            glue.config.channels = glueConfig.channels;
            glue.config.displays = glueConfig.displays;
        },
    };

    const currentLog: any[] = [];
    if (typeof window !== "undefined") {
        if (!(window as any).glueFactoryLog) {
            (window as any).glueFactoryLog = [];
        }
        (window as any).glueFactoryLog.push(currentLog);
    }

    function debugLog(entry: any) {
        currentLog.push(entry);
    }

    const glueApi = (await CoreFactory(options, ext)) as Glue42.Glue;

    // Decorate the ready glue object with extra functionalities coming from the user-defined libraries.
    if (Array.isArray(options?.libraries) && options.libraries.length) {
        await Promise.all(options.libraries.map((lib) => lib(glueApi, options)));
    }

    _browserEventsDispatcher.start(glueApi);

    return glueApi;
};

factory.coreVersion = (CoreFactory as any).version;
factory.version = version;
factory.calls = callInfo;

export default factory;
